import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { RubricForm } from "../RubricForm";
import { displayToast } from "../../../app/AppToast";
import { apiClient, useApiQuery } from "../../../../services/api";
import {
  closeAnnotation,
  pushAnnotationView,
  recordAnnotationVerdict,
  revalidateSubjectAnnotations,
  type AnnotationTarget,
  type SubjectAnnotation,
} from "../../../../domains/gpt/stores/annotationSheetStore";

/** Phase 1: our own answer, scored on the validated rubric. */
export function RateAnswerView({
  target,
}: {
  target: AnnotationTarget;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Arming the delete is form state, not component state (`useState` is banned in
  // business components) — and it IS armed rather than immediate, because this one
  // cascades: it takes this annotator's comparisons on the turn with it.
  const { watch, setValue } = useForm<{ confirmingDelete: boolean }>({
    defaultValues: { confirmingDelete: false },
  });
  const confirmingDelete = watch("confirmingDelete");

  // The id comes from the conversation query rather than the sheet's target: the
  // target is a snapshot taken when the sheet opened, and the rating may have been
  // created since — by the very submit below.
  const subject = target.subject;
  const conversationQuery = useApiQuery(
    "/webapp/private/conversations/:id",
    subject.engine === "legacy"
      ? { param: { id: subject.conversationId } }
      : false
  );
  const assistantQuery = useApiQuery(
    "/assistant/conversations/:conversationId/annotations",
    subject.engine === "assistant"
      ? { param: { conversationId: subject.conversationId } }
      : false
  );
  const annotations: SubjectAnnotation[] =
    subject.engine === "legacy"
      ? conversationQuery.state === "success"
        ? conversationQuery.body.myAnnotations.filter(
            (annotation) => annotation.turnIndex === subject.turnIndex
          )
        : []
      : assistantQuery.state === "success"
        ? assistantQuery.body.annotations.filter(
            (annotation) => annotation.runId === subject.runId
          )
        : [];
  const mine = annotations.find((annotation) => annotation.origin === "juisci");

  return (
    <RubricForm
      initial={target.existing}
      submitLabel={t("Submit")}
      // The secondary action is whichever one is TRUE of this turn.
      //
      // Not yet judged: "Skip", the honest escape for a turn you cannot rate.
      //
      // Already judged: "Compare". "Skip" there would silently replace the
      // ratings on screen with "I cannot judge this" — destructive, unlabelled,
      // one tap from Submit (and the server now refuses it outright when
      // comparisons exist). Going to the comparisons is what you actually want on
      // a reopened turn: without this the only route is Submit, which files a
      // revision of a verdict nobody changed.
      secondaryLabel={mine === undefined ? t("Skip") : t("Compare")}
      onSecondary={() => {
        pushAnnotationView({
          view: mine === undefined ? "skip" : "compare",
        });
      }}
      onSubmit={async (verdict) => {
        try {
          if (subject.engine === "legacy")
            await apiClient.post(
              "/webapp/private/conversations/:id/annotations",
              {
                param: { id: subject.conversationId },
                json: { turnIndex: subject.turnIndex, verdict },
              }
            );
          else
            await apiClient.post(
              "/assistant/conversations/:conversationId/annotations",
              {
                param: { conversationId: subject.conversationId },
                json: { runId: subject.runId, verdict },
              }
            );
          // Awaited, so there is no frame where the chat still shows the turn as
          // unevaluated after the write has landed.
          await revalidateSubjectAnnotations(subject);
          // The sheet keeps its own target, so it has to be told what was just
          // stored — otherwise stepping back here from the comparison reopens a
          // blank form over an existing verdict.
          recordAnnotationVerdict(verdict);
          pushAnnotationView({ view: "compare" });
        } catch {
          displayToast(t("Something went wrong. Please try again."), "error");
        }
      }}
    >
      <div className="flex flex-col gap-1 font-dm-sans">
        <span className="text-ui-note font-medium uppercase tracking-wide text-ds-slate-600">
          {t("The answer being evaluated")}
        </span>
        {/* The cited sources stay visible in the chat behind the sheet rather
            than being re-rendered inside it. */}
        <p className="line-clamp-3 text-ui-body font-normal text-ds-black">
          {target.answer}
        </p>
      </div>

      {mine === undefined ? null : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-ds-slate-200 px-4 py-3">
          <span className="font-dm-sans text-ui-note font-normal text-ds-slate-600">
            {confirmingDelete
              ? t("This also deletes the answers you compared on this turn.")
              : t("Remove my evaluation of this answer")}
          </span>
          {confirmingDelete ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setValue("confirmingDelete", false);
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  void (async () => {
                    try {
                      if (subject.engine === "legacy")
                        await apiClient.delete(
                          "/webapp/private/conversations/:id/annotations/:annotationId",
                          {
                            param: {
                              id: subject.conversationId,
                              annotationId: mine.id,
                            },
                          }
                        );
                      else
                        await apiClient.delete(
                          "/assistant/conversations/:conversationId/annotations/:annotationId",
                          {
                            param: {
                              conversationId: subject.conversationId,
                              annotationId: mine.id,
                            },
                          }
                        );
                      await revalidateSubjectAnnotations(subject);
                      closeAnnotation();
                    } catch {
                      displayToast(
                        t("Something went wrong. Please try again."),
                        "error"
                      );
                    }
                  })();
                }}
              >
                {t("Delete for good")}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setValue("confirmingDelete", true);
              }}
            >
              {t("Delete")}
            </Button>
          )}
        </div>
      )}
    </RubricForm>
  );
}
